import React, { useMemo } from "react";
import { Box, Divider, Switch, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FolderClosed, FolderTree, GraduationCap } from "lucide-react";
import SelectCst from "@components/InputCst/SelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import InputCst from "@components/InputCst/InputCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { useAttributesList } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/hooks/useAttributesList";
import {
  AttributeOption,
  EnrollType,
  ExpirationType,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/types";
import {
  ENROLL_TYPE_OPTIONS,
  EXPIRATION_TYPE_OPTIONS,
  isTopEnrollType,
  validateConfig,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/config/enrollmentFieldsConfig";
import { StaticGroupWizardData } from "../types";
import { StepContainer } from "../../Wizard/components/StepLayout";

const EnrollmentModeStep: React.FC<
  WizardStepProps<StaticGroupWizardData> & { hiddenEnrollTypes?: string[] }
> = ({ data, setData, hiddenEnrollTypes }) => {
  const { t } = useTranslation("group-wizard-v2");
  const { t: tEnroll } = useTranslation("enrollment-modal");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const { options: attributes } = useAttributesList(corporateId);

  const config = data.enrollConfig;
  const errors = validateConfig(config).errors;

  const update = (patch: Partial<typeof config>) =>
    setData((prev) => ({ enrollConfig: { ...prev.enrollConfig, ...patch } }));

  const counts = useMemo(() => {
    const acc = { activity: 0, course: 0, path: 0 };
    data.loSelections.forEach((s) => {
      if (s.type === "course") acc.course += 1;
      else if (s.type === "path") acc.path += 1;
      else acc.activity += 1;
    });
    return acc;
  }, [data.loSelections]);

  const recapSegments = [
    {
      count: counts.activity,
      label: t("enrollMode.activities"),
      Icon: GraduationCap,
    },
    {
      count: counts.course,
      label: t("enrollMode.courses"),
      Icon: FolderClosed,
    },
    { count: counts.path, label: t("enrollMode.paths"), Icon: FolderTree },
  ].filter((s) => s.count > 0);

  const enrollTypeOptions = ENROLL_TYPE_OPTIONS.filter(
    (o) => !hiddenEnrollTypes?.includes(o.value),
  ).map((o) => ({
    value: o.value,
    label: String(tEnroll(o.labelKey, o.value)),
  }));

  const isForcedExpirationDisabled =
    !isTopEnrollType(config.enrollType) ||
    config.expirationKind !== "fixed-date";

  const renderInlineSwitch = (
    label: string,
    checked: boolean,
    onChange: (next: boolean) => void,
    disabled = false,
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 0",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange(next)}
      />
    </Box>
  );

  return (
    <StepContainer>
      {recapSegments.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: "8px",
            p: 2,
            backgroundColor: theme.palette.action.hover,
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
            {t("enrollMode.recapPrefix")}
          </Typography>
          {recapSegments.map(({ count, label, Icon }, index) => (
            <React.Fragment key={label}>
              {index > 0 && (
                <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon size={18} color={theme.palette.primary.main} />
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {count} {label}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      )}

      <Box
        sx={{
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
        }}
      >
        <SelectCst
          id="group-enrollment-enrollType"
          label={tEnroll("config.enroll-type.label")}
          value={config.enrollType ?? ""}
          options={enrollTypeOptions}
          optionsToDisable={["AUTO", "BOTTOM"]}
          onChange={(v: any) => {
            const next = v as EnrollType;
            update({
              enrollType: next,
              ...(isTopEnrollType(next) ? {} : { forcedExpiration: false }),
            });
          }}
          required
          error={!!errors.enrollType}
          errorMessage={errors.enrollType ? t("validation.required") : ""}
        />
      </Box>

      <Divider />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {tEnroll("config.enable-expiration.label")}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.tertiary }}
          >
            {tEnroll("config.enable-expiration.helper")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "13px" }}>
            {tEnroll("common.no")}
          </Typography>
          <Switch
            checked={config.enableExpiration}
            onChange={(_, next) =>
              update(
                next
                  ? { enableExpiration: true, expirationKind: "fixed-date" }
                  : {
                      enableExpiration: false,
                      expirationKind: undefined,
                      expirationType: undefined,
                      expirationDate: undefined,
                      daysToComplete: undefined,
                      forcedExpiration: false,
                    },
              )
            }
          />
          <Typography sx={{ fontSize: "13px" }}>
            {tEnroll("common.yes")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {renderInlineSwitch(
          tEnroll("config.expiration-kind.options.completion-days-switch"),
          config.enableExpiration &&
            config.expirationKind === "completion-days",
          (next) =>
            update({
              expirationKind: next ? "completion-days" : "fixed-date",
              ...(next
                ? { expirationDate: undefined, forcedExpiration: false }
                : { daysToComplete: undefined }),
            }),
          !config.enableExpiration,
        )}

        {renderInlineSwitch(
          tEnroll("config.forced-expiration.label"),
          !!config.forcedExpiration && !isForcedExpirationDisabled,
          (next) => update({ forcedExpiration: next }),
          isForcedExpirationDisabled,
        )}

        <SelectCst
          id="group-enrollment-expirationType"
          label={tEnroll("config.expiration-type.label")}
          value={config.expirationType ?? ""}
          options={EXPIRATION_TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: String(tEnroll(o.labelKey, o.value)),
          }))}
          onChange={(v: any) => update({ expirationType: v as ExpirationType })}
          disabled={!config.enableExpiration}
          required={config.enableExpiration}
          error={!!errors.expirationType}
          errorMessage={errors.expirationType ? t("validation.required") : ""}
        />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <DataPickerCst
            id="group-enrollment-expirationDate"
            label={tEnroll("config.expiration-date.label")}
            value={config.expirationDate ?? null}
            onChange={(v: any) =>
              update({
                expirationDate: v
                  ? typeof v === "string"
                    ? v
                    : v?.toISOString?.()
                  : undefined,
              })
            }
            disabled={
              !config.enableExpiration || config.expirationKind !== "fixed-date"
            }
            required={
              config.enableExpiration && config.expirationKind === "fixed-date"
            }
            error={!!errors.expirationDate}
            errorMessage={errors.expirationDate ? t("validation.required") : ""}
          />

          <InputCst
            id="group-enrollment-daysToComplete"
            label={tEnroll("config.days-to-complete.label")}
            type="number"
            value={
              config.daysToComplete === undefined
                ? ""
                : String(config.daysToComplete)
            }
            onChange={(e: any) => {
              const raw = e?.target?.value;
              update({
                daysToComplete: raw === "" ? undefined : Number(raw),
              });
            }}
            disabled={
              !config.enableExpiration ||
              config.expirationKind !== "completion-days"
            }
            required={
              config.enableExpiration &&
              config.expirationKind === "completion-days"
            }
            error={!!errors.daysToComplete}
            errorMessage={errors.daysToComplete ? t("validation.required") : ""}
          />
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
          {tEnroll("config.attributes.label")}
        </Typography>
        <Typography
          sx={{ fontSize: "12px", color: theme.palette.text.tertiary, mb: 1 }}
        >
          {tEnroll("config.attributes.helper")}
        </Typography>
        <Box sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
          <MultiSelectCst
            id="group-enrollment-attributes"
            label={tEnroll("config.attributes.placeholder")}
            value={attributes.filter((a) =>
              config.attributesIds.includes(a.value),
            )}
            onChange={(next: any) => {
              const items = Array.isArray(next) ? next : [];
              update({
                attributesIds: items
                  .map((it: AttributeOption | number) =>
                    typeof it === "number" ? it : it?.value,
                  )
                  .filter((v): v is number => typeof v === "number"),
              });
            }}
            options={attributes}
          />
        </Box>
      </Box>
    </StepContainer>
  );
};

export default EnrollmentModeStep;
